// components/ui/SettingsShell.tsx
import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { ArrowLeft, Search, type LucideIcon } from '@/components/ui/lucide-memo';

export interface SettingsShellItem<Id extends string> {
  id: Id;
  label: string;
  icon: LucideIcon;
  /** A grey heading drawn above the first item of each group, as ChatGPT's 'Integrations'. */
  group?: string;
}

interface SettingsShellProps<Id extends string> {
  backLabel: string;
  onBack: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** A grey line above the sections naming what they belong to (an organisation's name). */
  groupLabel?: string;
  items: SettingsShellItem<Id>[];
  activeId: Id;
  onSelect: (id: Id) => void;
  lang?: 'en' | 'fr';
  /** Replaces a group's grey heading with a control, e.g. ChatGPT's workspace switcher. */
  groupHeading?: (group: string) => React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

// CHATGPT'S SETTINGS SCREEN (owner, 2026-09-29, chatgpt.com/settings: 'reproduce it exactly'), the
// frame shared by an organisation's settings and the admin console: the whole window, a black bar
// on the left with the way back in words, a search pill and the sections as plain rows; the page
// on the right is the caller's.
export function SettingsShell<Id extends string>({
  backLabel, onBack, search, onSearchChange, groupLabel, items, activeId, onSelect, lang = 'en', groupHeading, className, children,
}: SettingsShellProps<Id>) {
  // Opened on a section low in the list (Admin, from the left bar), the bar shows where you are.
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { activeRef.current?.scrollIntoView({ block: 'nearest' }); }, [activeId]);
  const query = search.trim().toLowerCase();
  const shown = query ? items.filter((item) => item.label.toLowerCase().includes(query)) : items;
  return (
    <div className={clsx('flex h-full min-h-0 bg-black text-[#ECECEC]', className)}>
      <aside className="flex w-[clamp(248px,23vw,348px)] shrink-0 flex-col border-r border-white/[0.06] px-1.5 pt-[62px]" aria-label={groupLabel ?? backLabel}>
        <button
          type="button"
          onClick={onBack}
          className="flex h-9 w-full items-center gap-3 rounded-[10px] px-3 text-left transition-colors hover:bg-[#1A1A1A]"
        >
          <ArrowLeft size={20} strokeWidth={1.6} className="shrink-0" />
          <span className="truncate" style={{ fontSize: 15 }}>{backLabel}</span>
        </button>

        <label className="mt-5 flex h-9 w-full cursor-text items-center gap-3 rounded-full bg-[#212121] px-3 text-[#8E8E8E]">
          <Search size={18} strokeWidth={1.6} className="shrink-0" />
          <input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={lang === 'fr' ? 'Rechercher' : 'Search'}
            aria-label={lang === 'fr' ? 'Rechercher une section' : 'Search sections'}
            className="min-w-0 flex-1 bg-transparent text-[#ECECEC] outline-none placeholder:text-[#8E8E8E]"
            style={{ fontSize: 15 }}
          />
        </label>

        <nav className="mt-1 flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">
          {groupLabel ? (
            <div className="flex h-11 items-center px-3 text-[#8E8E8E]">
              <span className="truncate" style={{ fontSize: 15 }} title={groupLabel}>{groupLabel}</span>
            </div>
          ) : <div className="h-3" />}
          {shown.map(({ id, label, icon: Icon, group }, index) => (
            <React.Fragment key={id}>
            {group && group !== shown[index - 1]?.group ? (
              groupHeading?.(group) ?? (
                <div className={clsx('flex h-11 items-end px-3 pb-2 text-[#8E8E8E]', index > 0 && 'mt-3')}>
                  <span className="truncate" style={{ fontSize: 15 }}>{group}</span>
                </div>
              )
            ) : null}
            <button
              ref={id === activeId ? activeRef : undefined}
              type="button"
              aria-current={id === activeId ? 'page' : undefined}
              onClick={() => onSelect(id)}
              className={clsx(
                'flex h-[37px] w-full shrink-0 items-center gap-3 rounded-[10px] px-3 text-left transition-colors',
                id === activeId ? 'bg-[#212121]' : 'hover:bg-[#1A1A1A]',
              )}
            >
              <Icon size={20} strokeWidth={1.6} className="shrink-0" />
              <span className="truncate" style={{ fontSize: 15 }}>{label}</span>
            </button>
            </React.Fragment>
          ))}
          {shown.length === 0 ? (
            <p className="px-3 py-2 text-[#8E8E8E]" style={{ fontSize: 14 }}>{lang === 'fr' ? 'Aucune section' : 'No section found'}</p>
          ) : null}
        </nav>
      </aside>

      <main className="relative flex min-w-0 flex-1 flex-col overflow-y-auto">{children}</main>
    </div>
  );
}
